import type { SurfaceInfo, TrackDef, TrackSample, Vec2 } from '../core/types';
import { angleDiff, clamp, dist } from '../core/math';

const CELL = 96;

interface Nearest {
  index: number;
  t: number;
  s: number;
  point: Vec2;
  dist: number;
  lateral: number;
}

const catmull = (p0: Vec2, p1: Vec2, p2: Vec2, p3: Vec2, t: number): Vec2 => {
  const t2 = t * t;
  const t3 = t2 * t;
  return {
    x:
      0.5 *
      (2 * p1.x + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
    y:
      0.5 *
      (2 * p1.y + (-p0.y + p2.y) * t + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3),
  };
};

/**
 * A pálya geometriája: zárt Catmull-Rom spline, ívhossz, felület- és fal-számítás.
 * A renderelés és a fizika is ezen keresztül kérdez.
 */
export class Track {
  readonly def: TrackDef;
  readonly samples: TrackSample[] = [];
  readonly totalLength: number;
  readonly corridorHalf: number;
  readonly bounds: { minX: number; minY: number; maxX: number; maxY: number };
  readonly startS: number;

  private readonly grid = new Map<number, number[]>();

  constructor(def: TrackDef) {
    this.def = def;
    this.corridorHalf = def.roadHalfWidth + def.kerbWidth + def.sidewalkWidth;

    const pts = def.centerline;
    if (pts.length < 4) throw new Error(`A(z) ${def.id} pályához legalább 4 vezérlőpont kell`);

    const raw: Vec2[] = [];
    for (let i = 0; i < pts.length; i++) {
      const p0 = pts[(i - 1 + pts.length) % pts.length];
      const p1 = pts[i];
      const p2 = pts[(i + 1) % pts.length];
      const p3 = pts[(i + 2) % pts.length];
      const steps = Math.max(3, Math.ceil(dist(p1, p2) / 8));
      for (let k = 0; k < steps; k++) raw.push(catmull(p0, p1, p2, p3, k / steps));
    }

    // ívhossz + tangens + normál + görbület
    const n = raw.length;
    let acc = 0;
    for (let i = 0; i < n; i++) {
      const prev = raw[(i - 1 + n) % n];
      const next = raw[(i + 1) % n];
      const p = raw[i];
      const tangent = { x: next.x - prev.x, y: next.y - prev.y };
      const tl = Math.hypot(tangent.x, tangent.y) || 1;
      tangent.x /= tl;
      tangent.y /= tl;
      const segLen = dist(p, raw[(i + 1) % n]);
      const nextTangent = { x: 0, y: 0 };
      {
        const nn = raw[(i + 2) % n];
        nextTangent.x = nn.x - p.x;
        nextTangent.y = nn.y - p.y;
        const l = Math.hypot(nextTangent.x, nextTangent.y) || 1;
        nextTangent.x /= l;
        nextTangent.y /= l;
      }
      this.samples.push({
        pos: p,
        tangent,
        normal: { x: -tangent.y, y: tangent.x },
        s: acc,
        curvature: segLen > 0.001 ? angleDiff(Math.atan2(nextTangent.y, nextTangent.x), Math.atan2(tangent.y, tangent.x)) / segLen : 0,
      });
      acc += segLen;
    }
    this.totalLength = acc;
    this.startS = def.startS * this.totalLength;

    for (let i = 0; i < this.samples.length; i++) {
      const s = this.samples[i].pos;
      const key = this.cellKey(s.x, s.y);
      const bucket = this.grid.get(key);
      if (bucket) bucket.push(i);
      else this.grid.set(key, [i]);
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    const pad = this.corridorHalf + 460;
    for (const s of this.samples) {
      minX = Math.min(minX, s.pos.x);
      minY = Math.min(minY, s.pos.y);
      maxX = Math.max(maxX, s.pos.x);
      maxY = Math.max(maxY, s.pos.y);
    }
    this.bounds = { minX: minX - pad, minY: minY - pad, maxX: maxX + pad, maxY: maxY + pad };
  }

  get length(): number {
    return this.samples.length;
  }

  private cellKey(x: number, y: number): number {
    return (Math.floor(x / CELL) + 4096) * 8192 + (Math.floor(y / CELL) + 4096);
  }

  /** 0..1 -> px ívhossz */
  sOf(progress: number): number {
    return ((progress % 1) + 1) % 1 * this.totalLength;
  }

  /** px ívhossz -> 0..1 */
  progressOfS(s: number): number {
    return (((s % this.totalLength) + this.totalLength) % this.totalLength) / this.totalLength;
  }

  /** előrehaladás ívhosszban a -> b (0..totalLength) */
  forward(fromS: number, toS: number): number {
    return (((toS - fromS) % this.totalLength) + this.totalLength) % this.totalLength;
  }

  sampleAt(progress: number): TrackSample {
    const p = ((progress % 1) + 1) % 1;
    const f = p * this.samples.length;
    return this.samples[Math.floor(f) % this.samples.length];
  }

  tangentAt(progress: number): Vec2 {
    const p = ((progress % 1) + 1) % 1;
    const f = p * this.samples.length;
    const i = Math.floor(f) % this.samples.length;
    const j = (i + 1) % this.samples.length;
    const t = f - Math.floor(f);
    const a = this.samples[i].tangent;
    const b = this.samples[j].tangent;
    return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
  }

  /** sima pozíció a középvonalon, oldalirányú eltolással (px) */
  posAt(progress: number, lateral = 0): Vec2 {
    const p = ((progress % 1) + 1) % 1;
    const f = p * this.samples.length;
    const i = Math.floor(f) % this.samples.length;
    const j = (i + 1) % this.samples.length;
    const t = f - Math.floor(f);
    const a = this.samples[i];
    const b = this.samples[j];
    const px = a.pos.x + (b.pos.x - a.pos.x) * t;
    const py = a.pos.y + (b.pos.y - a.pos.y) * t;
    const nx = a.normal.x + (b.normal.x - a.normal.x) * t;
    const ny = a.normal.y + (b.normal.y - a.normal.y) * t;
    return { x: px + nx * lateral, y: py + ny * lateral };
  }

  /** görbület px-enként, előjeles */
  curvatureAt(progress: number): number {
    const p = ((progress % 1) + 1) % 1;
    const i = Math.floor(p * this.samples.length) % this.samples.length;
    return this.samples[i].curvature;
  }

  /** legközelebbi középvonal-pont (szegmensre vetítve) */
  nearest(p: Vec2): Nearest {
    const cx = Math.floor(p.x / CELL);
    const cy = Math.floor(p.y / CELL);

    // 5×5 cella: a pálya szélén álló autó is megtalálja a valódi legközelebbi
    // szegmenst (korábban egyetlen cella alapján téves laterálist mértünk).
    let best: Nearest | null = null;
    for (let dx = -2; dx <= 2; dx++) {
      for (let dy = -2; dy <= 2; dy++) {
        const bucket = this.grid.get((cx + dx + 4096) * 8192 + (cy + dy + 4096));
        if (!bucket) continue;
        for (const idx of bucket) best = this.closer(best, idx, p);
      }
    }

    if (!best) {
      // durva sweep, majd finomítás (a pályától távoli pontokra)
      let bestIdx = 0;
      let bestD = Infinity;
      for (let i = 0; i < this.samples.length; i += 4) {
        const d = (this.samples[i].pos.x - p.x) ** 2 + (this.samples[i].pos.y - p.y) ** 2;
        if (d < bestD) {
          bestD = d;
          bestIdx = i;
        }
      }
      for (let k = -5; k <= 5; k++) {
        best = this.closer(best, (bestIdx + k + this.samples.length) % this.samples.length, p);
      }
    }

    return best as Nearest;
  }

  private closer(best: Nearest | null, index: number, p: Vec2): Nearest {
    const n = this.samples.length;
    const a = this.samples[index];
    const b = this.samples[(index + 1) % n];
    const abx = b.pos.x - a.pos.x;
    const aby = b.pos.y - a.pos.y;
    const segLen2 = abx * abx + aby * aby || 1;
    const t = clamp(((p.x - a.pos.x) * abx + (p.y - a.pos.y) * aby) / segLen2, 0, 1);
    const point = { x: a.pos.x + abx * t, y: a.pos.y + aby * t };
    const d = Math.hypot(p.x - point.x, p.y - point.y);
    if (best && d >= best.dist) return best;

    // a normált a vetítési pontban interpoláljuk (kanyarban ez számít)
    let nx = a.normal.x + (b.normal.x - a.normal.x) * t;
    let ny = a.normal.y + (b.normal.y - a.normal.y) * t;
    const nl = Math.hypot(nx, ny) || 1;
    nx /= nl;
    ny /= nl;
    const lateral = (p.x - point.x) * nx + (p.y - point.y) * ny;

    const segLen = Math.sqrt(segLen2);
    let s = a.s + segLen * t;
    if (s >= this.totalLength) s -= this.totalLength;
    return { index, t, s, point, dist: d, lateral };
  }

  surfaceAt(p: Vec2): SurfaceInfo {
    const hit = this.nearest(p);
    const lateral = hit.lateral;
    const abs = Math.abs(lateral);
    const d = this.def;
    let kind: SurfaceInfo['kind'] = 'road';
    if (abs > d.roadHalfWidth) kind = abs <= d.roadHalfWidth + d.kerbWidth ? 'kerb' : 'sidewalk';

    for (const pad of d.boostPads) {
      const padS = this.sOf(pad.s);
      const along = this.forward(padS, hit.s);
      if (along <= pad.length && Math.abs(lateral - pad.lateral) <= pad.width / 2) {
        kind = 'boost';
        break;
      }
    }

    return {
      kind,
      lateral,
      s: hit.s,
      closest: hit.point,
      dist: hit.dist,
      outside: abs > this.corridorHalf,
    };
  }

  /** az adott s-hez tartozó, kijelölt sávban lévő item-box pozíciók */
  itemBoxPositions(): Vec2[] {
    const out: Vec2[] = [];
    for (const row of this.def.itemRows) {
      const count = row.count ?? 4;
      const spacing = row.spacing ?? 64;
      for (let i = 0; i < count; i++) {
        const off = (i - (count - 1) / 2) * spacing;
        out.push(this.posAt(row.s, row.lateral + off));
      }
    }
    return out;
  }

  /** rajtfelállás: 2 oszlop, váltakozó sorok */
  gridPositions(count: number, spacingAlong = 78, spacingAcross = 58): { pos: Vec2; heading: number }[] {
    const out: { pos: Vec2; heading: number }[] = [];
    const start = this.progressOfS(this.startS - 260);
    for (let i = 0; i < count; i++) {
      const row = Math.floor(i / 2);
      const col = i % 2;
      const progress = this.progressOfS(this.sOf(start) - row * spacingAlong);
      const lateral = (col === 0 ? -1 : 1) * spacingAcross;
      const pos = this.posAt(progress, lateral);
      const tangent = this.tangentAt(progress);
      out.push({ pos, heading: Math.atan2(tangent.y, tangent.x) });
    }
    return out;
  }
}
